import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Upload,
  Eye,
  Users,
  Layers,
} from "lucide-react";
import { motion } from "framer-motion";

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  currentPage,
  setCurrentPage,
  onViewResumes,
}) => {
  return (
    <>
      {/* ─────────────────────── */}
      {/* COLLAPSED SIDEBAR      */}
      {/* ─────────────────────── */}
      {!sidebarOpen && (
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="
            fixed top-0 left-0 h-full w-14 
            bg-white/60 backdrop-blur-md border-r border-gray-200
            shadow-lg flex flex-col items-center py-4 
            space-y-7 z-50"
        >
          {/* Expand Button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md hover:bg-gray-100 text-gray-700 transition"
          >
            <ChevronRight size={22} />
          </button>

          {/* Nav Icons */}
          <div className="flex flex-col items-center space-y-7 mt-4">

            <CollapsedIcon
              icon={<Plus size={20} />}
              title="Add Role"
              onClick={() => setCurrentPage("admin")}
              active={currentPage === "admin"}
            />

            <CollapsedIcon
              icon={<Trash2 size={20} />}
              title="Delete Role"
              onClick={() => setCurrentPage("delete-role")}
              active={currentPage === "delete-role"}
            />

            <CollapsedIcon
              icon={<Upload size={20} />}
              title="Upload Resumes"
              onClick={() => setCurrentPage("upload")}
              active={currentPage === "upload"}
            />

            <CollapsedIcon
              icon={<Eye size={20} />}
              title="View Resumes"
              onClick={onViewResumes}
            />

            <CollapsedIcon
              icon={<Users size={20} />}
              title="Recruiter Agent"
              onClick={() => setCurrentPage("recruiter")}
              active={currentPage === "recruiter"}
            />
          </div>
        </motion.div>
      )}

      {/* ─────────────────────── */}
      {/* EXPANDED SIDEBAR        */}
      {/* ─────────────────────── */}
      {sidebarOpen && (
        <motion.aside
          initial={{ x: -260 }}
          animate={{ x: 0 }}
          exit={{ x: -260 }}
          transition={{ type: "spring", stiffness: 120, damping: 20 }}
          className="
            fixed top-0 left-0 h-full w-72 
            bg-white/75 backdrop-blur-xl 
            border-r border-gray-200 shadow-xl 
            flex flex-col z-50"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Layers size={24} className="text-orange-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Admin Panel
              </h2>
            </div>

            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-700"
            >
              <ChevronLeft size={24} />
            </button>
          </div>

          {/* Menu Items */}
          <nav className="p-5 space-y-4">
            <SidebarButton
              icon={<Plus size={20} />}
              label="Add Role"
              active={currentPage === "admin"}
              onClick={() => setCurrentPage("admin")}
            />

            <SidebarButton
              icon={<Trash2 size={20} />}
              label="Delete Role"
              active={currentPage === "delete-role"}
              onClick={() => setCurrentPage("delete-role")}
            />

            <SidebarButton
              icon={<Upload size={20} />}
              label="Upload Resumes"
              active={currentPage === "upload"}
              onClick={() => setCurrentPage("upload")}
            />

            <SidebarButton
              icon={<Eye size={20} />}
              label="View Resumes"
              onClick={onViewResumes}
            />

            <SidebarButton
              icon={<Users size={20} />}
              label="Recruiter Agent"
              active={currentPage === "recruiter"}
              onClick={() => setCurrentPage("recruiter")}
            />
          </nav>
        </motion.aside>
      )}
    </>
  );
};

/* ────────────────────────────── */
/* REUSABLE COMPONENTS            */
/* ────────────────────────────── */

// Expanded button style
const SidebarButton = ({ icon, label, active, onClick }) => (
  <motion.button
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    className={`
      relative w-full flex items-center gap-3 px-4 py-3 rounded-xl 
      font-medium transition-all
      ${active ? "bg-orange-600 text-white shadow-lg" : "bg-white/70 hover:bg-gray-100 text-gray-800"}
    `}
  >
    {active && (
      <motion.div
        layoutId="sidebar-active"
        className="absolute left-0 top-0 w-1 h-full bg-orange-500 rounded-r"
      />
    )}
    {icon}
    <span>{label}</span>
  </motion.button>
);

// Collapsed icon style
const CollapsedIcon = ({ icon, title, onClick, active }) => (
  <motion.button
    whileHover={{ scale: 1.15 }}
    whileTap={{ scale: 0.9 }}
    onClick={onClick}
    title={title}
    className={`
      p-2 rounded-lg transition 
      ${active ? "bg-orange-600 text-white shadow-md" : "text-gray-700 hover:bg-gray-100"}
    `}
  >
    {icon}
  </motion.button>
);

export default Sidebar;
